import { parseMemoFieldStorageDeep } from './programCellDisplayText';

export type ProgramLearningContentFields = {
  body: string;
  note: string;
  presentationRefId: string | null;
  worksheetRefId: string | null;
};

export type ProgramLearningContentRowLike = {
  learning_text?: string | null;
  learning_note?: string | null;
  presentation_ref_id?: string | null;
  worksheet_ref_id?: string | null;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeUuid(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  return v && UUID_RE.test(v) ? v : null;
}

function hasStructuredColumns(row: ProgramLearningContentRowLike): boolean {
  const note = (row.learning_note ?? '').trim();
  const pres = normalizeUuid(row.presentation_ref_id);
  const ws = normalizeUuid(row.worksheet_ref_id);
  const body = (row.learning_text ?? '').trim();
  if (note || pres || ws) return true;
  if (body && !body.startsWith('{')) return true;
  return false;
}

/** DB row 또는 upsert → 4필드 (컬럼 우선, 레거시 JSON fallback) */
export function readProgramLearningContentFields(
  row: ProgramLearningContentRowLike | null | undefined
): ProgramLearningContentFields {
  if (!row) {
    return { body: '', note: '', presentationRefId: null, worksheetRefId: null };
  }

  if (hasStructuredColumns(row)) {
    return {
      body: (row.learning_text ?? '').trim(),
      note: (row.learning_note ?? '').trim(),
      presentationRefId: normalizeUuid(row.presentation_ref_id),
      worksheetRefId: normalizeUuid(row.worksheet_ref_id),
    };
  }

  const parsed = parseMemoFieldStorageDeep(row.learning_text ?? '');
  return {
    body: parsed.text.trim(),
    note: parsed.note.trim(),
    presentationRefId: normalizeUuid(parsed.presentationRefId),
    worksheetRefId: normalizeUuid(parsed.worksheetRefId),
  };
}

/** 4필드 → DB 컬럼 (learning_text는 본문만, learning_note는 NOT NULL) */
export function writeProgramLearningContentFields(fields: ProgramLearningContentFields): {
  learning_text: string | null;
  learning_note: string;
  presentation_ref_id: string | null;
  worksheet_ref_id: string | null;
} {
  const body = (fields.body ?? '').trim();
  const note = (fields.note ?? '').trim();
  const presentationRefId = normalizeUuid(fields.presentationRefId);
  const worksheetRefId = normalizeUuid(fields.worksheetRefId);
  return {
    learning_text: body || null,
    learning_note: note,
    presentation_ref_id: presentationRefId,
    worksheet_ref_id: worksheetRefId,
  };
}

/** 편집기·LS 오버레이용 JSON (UI 전송 형식) */
export function serializeProgramLearningContentFieldsForEditor(
  fields: ProgramLearningContentFields
): string {
  const body = (fields.body ?? '').trim();
  const note = (fields.note ?? '').trim();
  const presentationRefId = normalizeUuid(fields.presentationRefId);
  const worksheetRefId = normalizeUuid(fields.worksheetRefId);
  if (!body && !note && !presentationRefId && !worksheetRefId) return '';
  if (!note && !presentationRefId && !worksheetRefId) return body;
  const payload: {
    text: string;
    note?: string;
    presentationRefId?: string;
    worksheetRefId?: string;
  } = { text: body };
  if (note) payload.note = note;
  if (presentationRefId) payload.presentationRefId = presentationRefId;
  if (worksheetRefId) payload.worksheetRefId = worksheetRefId;
  return JSON.stringify(payload);
}

/** 편집기·LS 오버레이 JSON → 4필드 */
export function parseProgramLearningContentFieldsFromEditor(
  raw: string | null | undefined
): ProgramLearningContentFields {
  const parsed = parseMemoFieldStorageDeep(raw ?? '');
  return {
    body: parsed.text.trim(),
    note: parsed.note.trim(),
    presentationRefId: normalizeUuid(parsed.presentationRefId),
    worksheetRefId: normalizeUuid(parsed.worksheetRefId),
  };
}

/**
 * 편집기·LS 오버레이 JSON을 "명시된 필드만" 추출한다.
 * - plain text: body만 override
 * - JSON: text/note/presentationRefId/worksheetRefId 중 존재하는 키만 override
 * - empty string: 4필드 전체 clear 의도
 */
export function parseProgramLearningContentOverridesFromEditor(
  raw: string | null | undefined
): Partial<ProgramLearningContentFields> {
  const value = (raw ?? '').trim();
  if (!value) {
    return {
      body: '',
      note: '',
      presentationRefId: null,
      worksheetRefId: null,
    };
  }

  if (!value.startsWith('{')) {
    return { body: value };
  }

  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    const out: Partial<ProgramLearningContentFields> = {};
    if (Object.prototype.hasOwnProperty.call(parsed, 'text')) {
      out.body = String(parsed.text ?? '').trim();
    }
    if (Object.prototype.hasOwnProperty.call(parsed, 'note')) {
      out.note = String(parsed.note ?? '').trim();
    }
    if (Object.prototype.hasOwnProperty.call(parsed, 'presentationRefId')) {
      out.presentationRefId = normalizeUuid(
        parsed.presentationRefId == null ? null : String(parsed.presentationRefId)
      );
    }
    if (Object.prototype.hasOwnProperty.call(parsed, 'worksheetRefId')) {
      out.worksheetRefId = normalizeUuid(
        parsed.worksheetRefId == null ? null : String(parsed.worksheetRefId)
      );
    }
    return out;
  } catch {
    return { body: value };
  }
}

/** upsert 직전: learning_text JSON이 섞여 있어도 4컬럼으로 정규화 */
export function normalizeProgramLearningContentColumnsForStorage<
  T extends ProgramLearningContentRowLike,
>(row: T): T {
  const fields = readProgramLearningContentFields(row);
  const written = writeProgramLearningContentFields(fields);
  return {
    ...row,
    learning_text: written.learning_text,
    learning_note: written.learning_note,
    presentation_ref_id: written.presentation_ref_id,
    worksheet_ref_id: written.worksheet_ref_id,
  };
}

export function mergeProgramLearningContentFields(
  ...sources: Array<ProgramLearningContentFields | ProgramLearningContentRowLike | string | null | undefined>
): ProgramLearningContentFields {
  let merged: ProgramLearningContentFields = {
    body: '',
    note: '',
    presentationRefId: null,
    worksheetRefId: null,
  };
  for (const src of sources) {
    if (src == null) continue;
    const next =
      typeof src === 'string'
        ? parseProgramLearningContentFieldsFromEditor(src)
        : 'body' in src && typeof (src as ProgramLearningContentFields).body === 'string'
          ? (src as ProgramLearningContentFields)
          : readProgramLearningContentFields(src as ProgramLearningContentRowLike);
    if (next.body.trim()) merged = { ...merged, body: next.body.trim() };
    if (next.note.trim()) merged = { ...merged, note: next.note.trim() };
    if (next.presentationRefId) merged = { ...merged, presentationRefId: next.presentationRefId };
    if (next.worksheetRefId) merged = { ...merged, worksheetRefId: next.worksheetRefId };
  }
  return merged;
}

export function applyLearningContentOverrides(
  baseline: ProgramLearningContentFields,
  overrides: Partial<ProgramLearningContentFields>
): ProgramLearningContentFields {
  const next = { ...baseline };
  if (Object.prototype.hasOwnProperty.call(overrides, 'body')) {
    next.body = (overrides.body ?? '').trim();
  }
  if (Object.prototype.hasOwnProperty.call(overrides, 'note')) {
    next.note = (overrides.note ?? '').trim();
  }
  if (Object.prototype.hasOwnProperty.call(overrides, 'presentationRefId')) {
    next.presentationRefId = normalizeUuid(overrides.presentationRefId);
  }
  if (Object.prototype.hasOwnProperty.call(overrides, 'worksheetRefId')) {
    next.worksheetRefId = normalizeUuid(overrides.worksheetRefId);
  }
  return next;
}

export function programLearningContentFieldsSignature(fields: ProgramLearningContentFields): string {
  return JSON.stringify({
    body: fields.body.trim(),
    note: fields.note.trim(),
    presentationRefId: fields.presentationRefId ?? '',
    worksheetRefId: fields.worksheetRefId ?? '',
  });
}

function learningFieldsAreEmpty(fields: ProgramLearningContentFields): boolean {
  return (
    !fields.body.trim() &&
    !fields.note.trim() &&
    !fields.presentationRefId &&
    !fields.worksheetRefId
  );
}

/**
 * 표 draft 재동기화: 첨부 ref는 유지하되, 아직 본문이 안 들어온 첫 페인트(빈 draft)를
 * 「사용자가 지운 칸」으로 오인해 이후 스코프 행 본문을 버리지 않는다.
 * 로컬 편집으로 비운 칸만 빈 값을 유지한다.
 */
export function resolveDraftLearningTextAfterBaselineArrival(params: {
  prevLearningText: string | undefined;
  nextLearningText: string;
  rowHasLocalEdits: boolean;
}): string {
  const { prevLearningText, nextLearningText, rowHasLocalEdits } = params;
  if (prevLearningText === undefined) return nextLearningText;
  const prevFields = parseProgramLearningContentFieldsFromEditor(prevLearningText);
  const nextFields = parseProgramLearningContentFieldsFromEditor(nextLearningText);
  if (learningFieldsAreEmpty(prevFields)) {
    if (!rowHasLocalEdits && !learningFieldsAreEmpty(nextFields)) return nextLearningText;
    return '';
  }
  const prevRefs = {
    presentationRefId: prevFields.presentationRefId,
    worksheetRefId: prevFields.worksheetRefId,
  };
  const nextRefs = {
    presentationRefId: nextFields.presentationRefId,
    worksheetRefId: nextFields.worksheetRefId,
  };
  if (
    (!prevRefs.presentationRefId || nextRefs.presentationRefId) &&
    (!prevRefs.worksheetRefId || nextRefs.worksheetRefId)
  ) {
    return nextLearningText;
  }
  return serializeProgramLearningContentFieldsForEditor({
    body: nextFields.body,
    note: nextFields.note,
    presentationRefId: nextFields.presentationRefId || prevFields.presentationRefId,
    worksheetRefId: nextFields.worksheetRefId || prevFields.worksheetRefId,
  });
}

/** DB row → 편집기·인디케이터용 editor JSON */
export function cellLearningTextForEditor(
  row: ProgramLearningContentRowLike | null | undefined
): string {
  return serializeProgramLearningContentFieldsForEditor(readProgramLearningContentFields(row));
}

/** 데이터 정리 모달 등 — 학습 본문 미리보기 */
export function auditLearningBodySnippet(
  row: ProgramLearningContentRowLike | string | null | undefined
): string {
  const fields =
    typeof row === 'string' || row == null
      ? parseProgramLearningContentFieldsFromEditor(row)
      : readProgramLearningContentFields(row);
  const text = fields.body.trim();
  if (!text) return '—';
  return text.length > 36 ? `${text.slice(0, 36)}…` : text;
}

export function clearPresentationRefFromLearningContentRow<T extends ProgramLearningContentRowLike>(
  row: T
): T {
  const fields = readProgramLearningContentFields(row);
  return { ...row, ...writeProgramLearningContentFields({ ...fields, presentationRefId: null }) };
}

/** DB row → draft `learning_text` (편집기 JSON). 본문 변환만 선택 적용 */
export function buildDraftLearningTextFromCell(
  cell: ProgramLearningContentRowLike | null | undefined,
  transformBody?: (body: string) => string
): string {
  const fields = readProgramLearningContentFields(cell);
  const body = transformBody ? transformBody(fields.body) : fields.body;
  return serializeProgramLearningContentFieldsForEditor({ ...fields, body });
}

export function resolveLearningAttachmentRefsFromSources(
  ...sources: Array<ProgramLearningContentRowLike | string | null | undefined>
): { presentationRefId?: string; worksheetRefId?: string } {
  const merged = mergeProgramLearningContentFields(...sources);
  return {
    presentationRefId: merged.presentationRefId ?? undefined,
    worksheetRefId: merged.worksheetRefId ?? undefined,
  };
}

export function clearWorksheetRefFromLearningContentRow<T extends ProgramLearningContentRowLike>(
  row: T
): T {
  const fields = readProgramLearningContentFields(row);
  return { ...row, ...writeProgramLearningContentFields({ ...fields, worksheetRefId: null }) };
}
